// "Featured in the Vibing Library" badge.
//
// One SVG, two themes, two sizes — served from
// /api/public/library/badge/<slug>.svg (?theme=light&size=sm) and pasted into
// other people's sites via the copy snippets below. Browser-safe: pure
// string building, no server imports.

export const BADGE_BASE = "https://vibing.inc";

export type BadgeTheme = "dark" | "light";
export type BadgeSize = "sm" | "md";

export const BADGE_DIMS: Record<BadgeSize, { w: number; h: number }> = {
  sm: { w: 196, h: 34 },
  md: { w: 236, h: 42 },
};

export function badgeSrc(slug: string, theme: BadgeTheme = "dark", size: BadgeSize = "md") {
  const params = new URLSearchParams();
  if (theme !== "dark") params.set("theme", theme);
  if (size !== "md") params.set("size", size);
  const qs = params.toString();
  return `${BADGE_BASE}/api/public/library/badge/${slug}.svg${qs ? `?${qs}` : ""}`;
}

/**
 * Same-origin badge URL for in-app previews. The absolute `badgeSrc` only
 * resolves once the production domain serves this build, so anything rendered
 * inside the app must use this relative path or the image breaks.
 */
export function badgePreviewSrc(slug: string, theme: BadgeTheme = "dark", size: BadgeSize = "md") {
  return badgeSrc(slug, theme, size).slice(BADGE_BASE.length);
}

export function badgeHref(slug: string) {
  return `${BADGE_BASE}/library/${slug}?ref=badge`;
}

export const BADGE_ALT = "Featured in the Vibing Library";

export function badgeHtml(slug: string, theme: BadgeTheme = "dark", size: BadgeSize = "md") {
  const { h } = BADGE_DIMS[size];
  return `<a href="${badgeHref(slug)}" target="_blank" rel="noopener">
  <img src="${badgeSrc(slug, theme, size)}" alt="${BADGE_ALT}" height="${h}" style="height:${h}px;width:auto" loading="lazy" />
</a>`;
}

export function badgeMarkdown(slug: string, theme: BadgeTheme = "dark", size: BadgeSize = "md") {
  return `[![${BADGE_ALT}](${badgeSrc(slug, theme, size)})](${badgeHref(slug)})`;
}

export function badgeJsx(slug: string, theme: BadgeTheme = "dark", size: BadgeSize = "md") {
  const { h } = BADGE_DIMS[size];
  return `<a href="${badgeHref(slug)}" target="_blank" rel="noopener">
  <img
    src="${badgeSrc(slug, theme, size)}"
    alt="${BADGE_ALT}"
    height={${h}}
    style={{ height: ${h}, width: "auto" }}
    loading="lazy"
  />
</a>`;
}

/** The SVG itself — rendered by the badge endpoint. */
export function renderBadgeSvg(theme: BadgeTheme = "dark", size: BadgeSize = "md") {
  const { w, h } = BADGE_DIMS[size];
  const dark = theme === "dark";
  const bg = dark ? "#0d0d10" : "#ffffff";
  const ring = dark ? "#2a2a31" : "#e4e4e8";
  const muted = dark ? "#9a9aa4" : "#6c6c76";
  const fg = dark ? "#f6f6f7" : "#0d0d10";
  const scale = h / 42;
  const gemX = 14 * scale;
  const textX = 40 * scale;
  const small = 9.5 * scale;
  const big = 13 * scale;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="${BADGE_ALT}">
  <title>${BADGE_ALT}</title>
  <rect x="0.5" y="0.5" width="${w - 1}" height="${h - 1}" rx="${h / 2}" fill="${bg}" stroke="${ring}"/>
  <g transform="translate(${gemX}, ${h / 2}) scale(${scale})" fill="none" stroke="${fg}" stroke-width="1.5" stroke-linejoin="round">
    <path d="M-8 -3 L-5 -7 H5 L8 -3 L0 7 Z"/>
    <path d="M-8 -3 H8"/>
  </g>
  <text x="${textX}" y="${h / 2 - 3 * scale}" font-family="ui-sans-serif, -apple-system, Segoe UI, Helvetica, Arial, sans-serif" font-size="${small}" fill="${muted}" letter-spacing="${0.6 * scale}">FEATURED IN</text>
  <text x="${textX}" y="${h / 2 + 11 * scale}" font-family="ui-sans-serif, -apple-system, Segoe UI, Helvetica, Arial, sans-serif" font-size="${big}" font-weight="600" fill="${fg}">The Vibing Library</text>
</svg>`;
}
